<template>
  <a-tooltip v-if="showTip" title="New template update available">
    <div
      class="template-version-tip"
      :class="{ disabled: updating }"
      role="button"
      tabindex="0"
      @click="handleUpdate"
      @keydown.enter.prevent="handleUpdate"
    >
      <span class="tip-icon-wrap">
        <SyncOutlined class="tip-sync" :spin="updating" />
        <ArrowUpOutlined class="tip-arrow" />
      </span>
      <span class="tip-dot" />
    </div>
  </a-tooltip>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import { SyncOutlined, ArrowUpOutlined } from '@ant-design/icons-vue'
import { useRoute } from 'vue-router'
import {
  applyNewTemplate,
  getApplyNewTemplateStatus,
  type TemplateVersionStatus,
} from '@/api/templateVersion'
import { getShopId } from '@/utils/shop'
import { getCurrentShopId } from '@/utils/shopifyAuth'

const route = useRoute()
const status = ref<TemplateVersionStatus | null>(null)
const loading = ref(false)
const updating = ref(false)
const dismissed = ref(false)

const resolveShopifyId = () => {
  const fromRoute = getCurrentShopId() || getShopId(route)
  if (fromRoute) return String(fromRoute).split('.')[0]
  return ''
}

/** isNewVersion === false means the store is not on the latest template. */
const showTip = computed(() => {
  if (dismissed.value || loading.value || !status.value) return false
  return status.value.isNewVersion === false
})

const checkStatus = async () => {
  const shopifyId = resolveShopifyId()
  if (!shopifyId) {
    status.value = null
    return
  }

  loading.value = true
  try {
    status.value = await getApplyNewTemplateStatus(shopifyId)
    dismissed.value = false
  } catch (error) {
    console.error('Failed to check template version:', error)
    status.value = null
  } finally {
    loading.value = false
  }
}

const handleUpdate = async () => {
  if (updating.value) return

  const shopifyId = resolveShopifyId()
  if (!shopifyId) {
    message.error('Missing shop ID')
    return
  }

  updating.value = true
  try {
    await applyNewTemplate(shopifyId, { source: 'p' })
    message.success('Template updated successfully')
    await checkStatus()
    if (status.value?.isNewVersion !== false) {
      dismissed.value = true
    }
  } catch (error: any) {
    message.error(error?.message || 'Failed to update template')
  } finally {
    updating.value = false
  }
}

onMounted(() => {
  checkStatus()
})

watch(
  () => route.query.shop,
  () => {
    checkStatus()
  },
)
</script>

<style scoped>
.template-version-tip {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  box-sizing: border-box;
  color: #2f7cfa;
  cursor: pointer;
  user-select: none;
  line-height: 0;
}

.template-version-tip:hover {
  color: #4b8efc;
}

.template-version-tip.disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.tip-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.tip-sync {
  font-size: 20px;
}

.tip-arrow {
  position: absolute;
  font-size: 10px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.tip-dot {
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff4d4f;
  box-shadow: 0 0 0 1.5px #fff;
  pointer-events: none;
}
</style>
